import { EventSource } from "eventsource";
import { throttle } from "lodash";
import { storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import config from "@/config";
import { Artefact, type BpmnArtefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import {
    BodyStartProcessProcessMgtGenerateDefinitionXmlStartPost,
    StartProcessProcessMgtGenerateDefinitionXmlStartPostData,
} from "@/lib/openapi/gen";
// Use direct import since there is a circular dependency in @hey-api which
// causes problems when importing `schemas` from @/lib/openapi/index.
import { Body_start_process_process_mgt_generate_definition_xml_start_postSchema } from "@/lib/openapi/gen/schemas.gen";
import { followLink } from "@/lib/openapi/helpers";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { Process } from "@/stores/processes-store";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";

export type { Process } from "@/stores/processes-store";

export const PROMPT_MAX_LENGTH =
    Body_start_process_process_mgt_generate_definition_xml_start_postSchema
        .properties.prompt.maxLength;

// Uses the current route to determine what the 'process' should be and fetches
// the process description for you.
export const useProcessStore = defineEntityStore("process", () => {
    const router = useRouter();
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();
    const { getToken } = useAuthenticationStore();

    const toast = useToast();
    const process = ref<Process>();
    const description = ref<Artefact<"text/markdown">>();
    const definition = ref<BpmnArtefact | undefined>();
    const syncStore = useSyncStore();
    const entityId = computed(() => process.value?.identity);
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.PROCESS,
        attribute: "description",
        artefact: description,
    });
    const definitionDraft = useDraftManager({
        entityId,
        entityType: EntityType.PROCESS,
        attribute: "process_definition",
        artefact: definition,
    });
    discardDraftsAfterSync(
        entityId,
        [descriptionDraft, definitionDraft],
        EntityType.PROCESS
    );

    const { process: processInfo } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );

    const isFetchingProcess = ref(false);
    const isFetchingDescription = ref(false);
    const isFetchingDefinition = ref(false);
    const generatingProcesses = ref<Record<string, boolean>>({});
    const isGeneratingBpmn = computed(() => {
        // processInfo.value?.identity always reflects the current route's process.
        // Fall back to process.value?.identity only when entity store hasn't loaded yet.
        const id = processInfo.value?.identity ?? process.value?.identity;
        if (!id) return false;
        // Guard process.value?.is_generating against stale data from a different process.
        return (
            (!!process.value?.is_generating &&
                process.value?.identity === id) ||
            !!generatingProcesses.value[id]
        );
    });
    const isAnyGenerating = computed(() =>
        Object.values(generatingProcesses.value).some(Boolean)
    );
    const controller = ref<AbortController>(new AbortController());

    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(process);

    async function updateProcess(force = false) {
        const processId = processInfo.value?.identity;

        // In case the same process is selected, return early.
        if (!force && process.value && process.value.identity === processId) {
            return;
        }

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        // If no process id is set
        if (!processId) {
            description.value = undefined;
            process.value = undefined;
            return;
        }

        isFetchingProcess.value = true;
        description.value = undefined;
        definition.value = undefined;
        process.value = undefined;

        try {
            const { response, data } =
                await api.startProcessProcessMgtGetProcessStartPost({
                    body: {
                        process_id: `${processId}`,
                        revision: releaseStore.activeRevision ?? undefined,
                    },
                    signal: controller.value.signal,
                });

            if (!response.ok) {
                if (response.status === 404) {
                    if (!releaseStore.activeRevision) {
                        router.replace({
                            name: "not-found",
                            params: {
                                orgName: organization.value?.name,
                                stekName: stek.value?.name,
                            },
                            query: {
                                type: "process",
                                id: processId,
                            },
                        });
                    }
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            process.value = data;

            // Sync generating state from backend — the backend is the source
            // of truth so we don't rely solely on SSE connection state.
            if (data?.is_generating) {
                generatingProcesses.value[processId] = true;
            } else if (generatingProcesses.value[processId]) {
                generatingProcesses.value[processId] = false;
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                return;
            } else {
                throw e;
            }
        } finally {
            isFetchingProcess.value = false;
        }
        // Fetch description and definition while we're still in the try block before setting isFetchingProcess=false
        if (process.value) {
            await Promise.all([
                updateDescription(processId),
                updateDefinition(processId),
            ]);
        }
    }
    watch(
        () => processInfo.value?.identity,
        () => updateProcess(true)
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (process.value) updateProcess(true);
        }
    );

    /**
     * Lightweight backend check for is_generating — only updates the
     * generatingProcesses flag without resetting process/definition state.
     * Used by the polling watcher to avoid disrupting the page during generation.
     */
    const refreshGeneratingState = async () => {
        const processId = processInfo.value?.identity;
        if (!processId) return;
        try {
            const { response, data } =
                await api.startProcessProcessMgtGetProcessStartPost({
                    body: {
                        process_id: `${processId}`,
                        revision: releaseStore.activeRevision ?? undefined,
                    },
                });
            if (!response.ok || !data) return;
            if (data.is_generating) {
                generatingProcesses.value[processId] = true;
            } else if (generatingProcesses.value[processId]) {
                generatingProcesses.value[processId] = false;
            }
            // Keep process.value.is_generating in sync so the computed reflects
            // the latest backend state without a full page re-fetch.
            if (process.value?.identity === processId) {
                process.value.is_generating = data.is_generating ?? false;
            }
        } catch {
            // Swallow errors — polling should not disrupt the user.
        }
    };

    // Poll the backend every 3 seconds while generation is in progress so that
    // the generating state stays accurate after SSE reconnections or navigation.
    // A single interval is shared — the null guard prevents multiple concurrent
    // polling loops from accumulating across navigations or flag restores.
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    watch(
        isGeneratingBpmn,
        (generating) => {
            if (generating && !pollInterval) {
                pollInterval = setInterval(async () => {
                    if (!isGeneratingBpmn.value) {
                        clearInterval(pollInterval!);
                        pollInterval = null;
                        return;
                    }
                    await refreshGeneratingState();
                }, 3000);
            } else if (!generating && pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
            }
        },
        { immediate: true }
    );

    const updateDescription = async (currentProcessId?: string | undefined) => {
        if (process.value) {
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    process.value,
                    "processDescription",
                    controller.value.signal,
                    revisionQuery.value
                );

                // Check if process has changed during the async operation
                if (
                    !process.value ||
                    process.value.identity !== currentProcessId
                ) {
                    console.debug(
                        "Process changed during description fetch, aborting description update"
                    );
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingDescription.value = false;
            }
        }
    };

    const updateDefinition = async (currentProcessId?: string | undefined) => {
        if (process.value) {
            if (process.value.process_definition) {
                isFetchingDefinition.value = true;
                try {
                    const { response, data } = await followLink<File>(
                        process.value.process_definition,
                        "get",
                        undefined,
                        controller.value.signal,
                        revisionQuery.value
                    );

                    if (!response.ok) {
                        if (response.status === 404) {
                            return;
                        }
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }

                    // Check if process has changed during the async operation
                    if (
                        !process.value ||
                        process.value.identity !== currentProcessId
                    ) {
                        console.debug(
                            "Process changed during definition fetch, aborting definition update"
                        );
                        return;
                    }

                    definition.value = await Artefact.createBpmnArtefact({
                        name: "processDefinition",
                        identity: process.value.process_definition.identity,
                        blob: data!,
                    });

                    // Check again if process has changed during artefact creation
                    if (
                        !process.value ||
                        process.value.identity !== currentProcessId
                    ) {
                        console.debug(
                            "Process changed during artefact creation, aborting definition update"
                        );
                        return;
                    }

                    definition.value.links =
                        process.value.process_definition.links;
                } finally {
                    isFetchingDefinition.value = false;
                }
            } else {
                // Check if process has changed before setting an empty artefact
                if (
                    !process.value ||
                    process.value.identity !== currentProcessId
                ) {
                    console.debug(
                        "Process changed before creating empty artefact, aborting"
                    );
                    return;
                }

                definition.value = Artefact.createEmptyArtefact({
                    mimetype: "application/bpmn+xml",
                    identity: process.value.identity + "_definition",
                });
            }
        }
    };

    /**
     * Generate a process definition as raw BPMN XML directly.
     */
    const generateProcessDefinitionXml = async (
        prompt: string,
        callbacks?: {
            onStarted?: () => void;
            onProgress?: () => void;
        }
    ): Promise<string | undefined> => {
        const targetProcessId = process.value!.identity;
        generatingProcesses.value[targetProcessId] = true;
        const token = await getToken();
        const body: BodyStartProcessProcessMgtGenerateDefinitionXmlStartPost = {
            prompt,
            process_id: targetProcessId,
        };
        // Set so that any change in the type will result in a type error.
        const url: StartProcessProcessMgtGenerateDefinitionXmlStartPostData["url"] =
            "/process/process_mgt/generate_definition_xml/start";
        const eventSource = new EventSource(`${config.bekendURI}${url}`, {
            fetch: (input, init) => {
                return fetch(input, {
                    ...init,
                    body: JSON.stringify(body),
                    method: "POST",
                    headers: {
                        ...init?.headers,
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                        Organization: organization?.value?.identity ?? "",
                    },
                });
            },
        });

        eventSource.addEventListener("open", () => {
            callbacks?.onStarted?.();
        });

        return await new Promise<string | undefined>((resolve, reject) => {
            eventSource.addEventListener("message", async ({ data }) => {
                try {
                    const parsedData = JSON.parse(data);
                    callbacks?.onProgress?.();
                    if (parsedData.status === "generating") {
                        callbacks?.onProgress?.();
                    } else if (parsedData.status === "completed") {
                        generatingProcesses.value[targetProcessId] = false;
                        resolve(parsedData.bpmn_xml);
                    } else if (parsedData.status === "error") {
                        generatingProcesses.value[targetProcessId] = false;
                        reject(new Error(parsedData.detail ?? "Unknown error"));
                    }
                } catch {
                    // Not a regular message with parseable content.
                    toast.add({
                        severity: "error",
                        summary: `Got invalid information to continue XML generation.`,
                    });
                    generatingProcesses.value[targetProcessId] = false;
                    resolve(undefined);
                }
            });

            eventSource.addEventListener("close", () => {
                // Stream ended naturally; resolve as no-op if not already settled.
                resolve(undefined);
            });

            eventSource.addEventListener("error", (error) => {
                if (error.code) {
                    // HTTP 4xx/5xx or other real server error — report and reject.
                    generatingProcesses.value[targetProcessId] = false;
                    toast.add({
                        severity: "error",
                        summary: `Could not complete BPMN generation`,
                        detail: `${error.code}: ${error.message}`,
                    });
                    reject(error);
                } else {
                    // Spurious reconnect/connection-drop event from the eventsource
                    // package — could be navigation away or stream end. Do NOT clear
                    // the generating flag here; polling will clear it once the backend
                    // confirms generation is done.
                    resolve(undefined);
                }
            });
        }).finally(() => {
            eventSource.close();
        });
    };

    const isModified = computed(() => {
        if (isFetchingProcess.value) {
            return false;
        }
        return Boolean(
            isNameModified.value ||
                description.value?.isModified ||
                definition.value?.isModified
        );
    });

    const queueSave = async () => {
        if (!isModified.value || !process.value) {
            return;
        }

        const updateContent: types.StartProcessProcessMgtUpdateProcessStartPostData =
            {
                body: {
                    process_id: process.value.identity,
                },
                url: "/process/process_mgt/update_process/start",
            };

        if (isNameModified.value) {
            updateContent.body.name = process.value.name;
        }

        if (definition.value?.isModified) {
            updateContent.body.process_definition = definition.value.blob;
            updateContent.body.process_definition_preview =
                definition.value.preview;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            !updateContent.body.process_id ||
            Object.keys(updateContent.body).length === 1
        ) {
            // Only process_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.PROCESS,
            args: [updateContent],
        });

        if (definition.value?.isModified) {
            definition.value.persist();
        }
        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };

    /**
     * Bypasses the inactivity debounce to immediately sync the current BPMN
     * process definition to the backend. Used when a new shape is added to the
     * diagram so the new element becomes navigatable near-instantly.
     */
    const triggerImmediateSync = async () => {
        await queueSave();
        if (process.value?.identity) {
            await syncStore.sync(process.value.identity);
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        await definition.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);

    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.PROCESS &&
                identity.entityId === process.value?.identity
            ) {
                if (stek.value) {
                    updateProcess(true);
                }
            }
        }
    );

    return {
        process,
        description,
        definition,
        generateProcessDefinitionXml,
        updateDefinition,
        updateProcess,
        isGeneratingBpmn,
        isAnyGenerating,
        isFetching: computed(() =>
            Boolean(
                isFetchingProcess.value ||
                    isFetchingDefinition.value ||
                    isFetchingDescription.value
            )
        ),
        isModified,
        reset,
        triggerImmediateSync,
    };
});

export const useProcessManager = () => storeToRefs(useProcessStore());
